import { lazy, Suspense, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import FullSizeLoading from "components/FullSizeLoading";
import useAcceptServiceEstimate from "hooks/api/useAcceptServiceEstimate";
import useCancelOption from "hooks/api/useCancelOption";
import useDownloadServiceEstimatePdf from "hooks/api/useDownloadServiceEstimatePdf";
import useFindOption from "hooks/api/useFindOption";
import useFindRequestServiceById from "hooks/api/useFindRequestServiceById";
import useRejectServiceEstimate from "hooks/api/useRejectServiceEstimate";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useInfoModal, useWarningModal } from "hooks/useModal";
import usePdfDownloadProvider from "hooks/usePdfDownloadProvider";
import { EstimateWip, OptionDetail, RequestService } from "pages/OptionDetail/constants";
import { isBrowser, isTablet } from "utils/deviceDetect";

const ServiceDetailPresenter = lazy(() =>
  isBrowser
    ? import("pages/ServiceDetail/pc/ServiceDetailPresenter")
    : isTablet
      ? import("pages/ServiceDetail/sp/ServiceDetailPresenter")
      : import("pages/ServiceDetail/sp/ServiceDetailPresenter")
);

export type ViewState = "list" | "accept" | "reject";

export type ServiceDetailPresenterProps = {
  option: OptionDetail;
  service: RequestService;
  viewState: ViewState;
  selectedEstimate: EstimateWip | null;
  optionDetailModalVisible: boolean;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  setSelectedEstimate: React.Dispatch<React.SetStateAction<EstimateWip | null>>;
  handleNavigateServiceList: () => void;
  handleCancelOption: () => void;
  handleNavigateOptionEdit: (optionId: string) => void;
  handleRejectEstimate: () => void;
  handleAcceptEstimate: () => void;
  handleClickDownloadPdf: (estimateId: string) => void;
  handleOpenDetailModal: (optionId: string) => void;
  handleCloseModal: () => void;
  formValues: {
    rejectedReason: string;
    remarks: string;
    setRejectedReason: React.Dispatch<React.SetStateAction<string>>;
    setRemarks: React.Dispatch<React.SetStateAction<string>>;
    errors: {
      isErrorRemarks: boolean;
    };
  };
};

const ServiceDetailContainer = () => {
  const [infoModalState, setInfoModalState] = useState<{ title: string }>({ title: "" });
  const [warningModalState, setWarningModalState] = useState<{ title: string; label: string; onClick: () => void }>({
    title: "",
    label: "",
    onClick: () => null,
  });
  const [viewState, setViewState] = useState<ViewState>("list");
  const [selectedEstimate, setSelectedEstimate] = useState<EstimateWip | null>(null);
  const [rejectedReason, setRejectedReason] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [optionDetailModalVisible, setOptionDetailModalVisible] = useState(false);

  const { id, serviceId } = useParams() as { id: string; serviceId: string };
  const { option, mutate } = useFindOption(id);
  const { downloadServiceEstimatePdfByEstimateId } = usePdfDownloadProvider();
  const navigate = useNavigate();
  const { cancelOption } = useCancelOption();
  const { showInfoModal, InfoModal } = useInfoModal();
  const { showWarningModal, hideWarningModal, WarningModal } = useWarningModal();
  const { showErrorModal } = useErrorModalStatusContext();
  const { requestService, mutate: mutateRequestService } = useFindRequestServiceById(id, serviceId);
  const { acceptEstimate } = useAcceptServiceEstimate();
  const { rejectEstimate } = useRejectServiceEstimate();

  const isErrorRemarks = rejectedReason.length === 0;

  const formValues = {
    rejectedReason,
    remarks,
    setRejectedReason,
    setRemarks,
    errors: {
      isErrorRemarks,
    },
  };

  const cancelOptionHandler = async () => {
    const status = await cancelOption(id).catch(() => {
      showErrorModal({ httpMethod: "put" });
      hideWarningModal();
    });
    if (status === 200) {
      hideWarningModal();
      openCompleteModal();
      await mutate();
    }
  };

  const openSubmitModal = () => {
    setWarningModalState({
      title: "依頼を停止しますか？",
      label: "停止する",
      onClick: cancelOptionHandler,
    });
    showWarningModal();
  };

  const openCompleteModal = () => {
    setInfoModalState({
      title: "依頼を停止しました",
    });
    showInfoModal();
  };
  const openAcceptedModal = async () => {
    setInfoModalState({
      title: "依頼しました",
    });
    showInfoModal();
    setViewState("list");
    setSelectedEstimate(null);
    setRemarks("");
    await mutateRequestService();
  };
  const openRejectedModal = async () => {
    setInfoModalState({
      title: "見送りました",
    });
    showInfoModal();
    setViewState("list");
    setSelectedEstimate(null);
    setRejectedReason("");
    await mutateRequestService();
  };
  const handleCancelOption = () => {
    openSubmitModal();
  };
  const handleNavigateServiceList = () => {
    navigate(`/option/${id}/`);
  };
  const handleNavigateOptionEdit = () => {
    navigate(`/option/${id}/update`);
  };
  const handleRejectEstimate = async () => {
    await rejectEstimate(id, serviceId, selectedEstimate?.id || "", rejectedReason);
    openRejectedModal();
  };
  const handleAcceptEstimate = async () => {
    await acceptEstimate(id, serviceId, selectedEstimate?.id || "", remarks);
    openAcceptedModal();
  };
  const handleClickDownloadPdf = (estimateId: string) => {
    downloadServiceEstimatePdfByEstimateId(id, serviceId, estimateId);
  };
  const handleOpenDetailModal = () => {
    setOptionDetailModalVisible(true);
  };
  const handleCloseModal = () => {
    setOptionDetailModalVisible(false);
  };

  return (
    <Suspense fallback={<FullSizeLoading />}>
      <ServiceDetailPresenter
        option={option}
        service={requestService}
        viewState={viewState}
        setViewState={setViewState}
        selectedEstimate={selectedEstimate}
        setSelectedEstimate={setSelectedEstimate}
        handleCancelOption={handleCancelOption}
        handleNavigateOptionEdit={handleNavigateOptionEdit}
        handleNavigateServiceList={handleNavigateServiceList}
        handleRejectEstimate={handleRejectEstimate}
        handleAcceptEstimate={handleAcceptEstimate}
        handleClickDownloadPdf={handleClickDownloadPdf}
        formValues={formValues}
        handleOpenDetailModal={handleOpenDetailModal}
        optionDetailModalVisible={optionDetailModalVisible}
        handleCloseModal={handleCloseModal}
      />
      <WarningModal
        title={warningModalState.title}
        label={warningModalState.label}
        onClick={warningModalState.onClick}
      />
      <InfoModal title={infoModalState.title} />
    </Suspense>
  );
};

export default ServiceDetailContainer;
